import { useEffect, useState } from 'react';
import { Gift, Copy, Check, Users } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n';
import { toast } from 'sonner';
import ShareButton from './ShareButton';

export default function InviteCard() {
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/referrals/me')
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !data?.code) return null;

    const link = `${window.location.origin}/register?ref=${data.code}`;
    const shareText = `${t('invite.share_text')}\n\n${link}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success(t('invite.copy_ok'));
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(t('invite.copy_fail'));
        }
    };

    const invited = data.invited_count || 0;
    const paying = data.paying_count || 0;
    const threshold = data.threshold || 5;
    const daysEarned = data.days_earned || 0;
    const towardNext = data.toward_next || 0;
    const needForNext = data.need_for_next || threshold;
    const pct = Math.min(100, (towardNext / threshold) * 100);

    return (
        <section className="mb-8" data-testid="invite-card">
            <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                <Gift size={11} /> {t('invite.header')}
            </h2>
            <div className="bg-gradient-to-br from-brand-green/10 to-brand-sage/15 border border-brand-green/30 rounded-3xl p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-2xl bg-brand-green/20 text-brand-green flex items-center justify-center flex-shrink-0">
                        <Gift size={18} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1">
                        <div className="font-serif text-base text-brand-ink leading-snug">
                            {t('invite.share_pre')} <strong>{t('invite.share_strong')}</strong>
                        </div>
                        <div className="text-xs text-brand-ink3 mt-1">
                            {t('invite.share_sub')}
                        </div>
                    </div>
                </div>

                <div className="bg-white/60 dark:bg-brand-card border border-brand-border rounded-2xl px-3 py-2.5 flex items-center gap-2 mb-3">
                    <code data-testid="invite-link" className="flex-1 text-xs text-brand-ink2 truncate select-all">{link}</code>
                    <button
                        data-testid="btn-copy-invite"
                        onClick={copy}
                        className="inline-flex items-center gap-1 bg-brand-green text-white text-xs px-3 py-1.5 rounded-full hover:bg-brand-greenDark transition-colors flex-shrink-0"
                    >
                        {copied ? <><Check size={12} /> {t('invite.copied')}</> : <><Copy size={12} /> {t('invite.copy')}</>}
                    </button>
                </div>

                <div className="w-full mb-4">
                    <ShareButton
                        title={t('invite.share_title')}
                        text={shareText}
                        url={link}
                        label={t('invite.share_label')}
                        className="w-full justify-center bg-brand-card border border-brand-border text-brand-ink py-2.5 rounded-full hover:bg-brand-bg2"
                    />
                </div>

                <div className="border-t border-brand-border/60 pt-3 grid grid-cols-3 gap-2 text-center">
                    <div>
                        <div className="text-xs text-brand-ink3 inline-flex items-center gap-1"><Users size={11} /> {t('invite.invited')}</div>
                        <div className="font-serif text-xl text-brand-ink mt-0.5" data-testid="invite-invited-count">{invited}</div>
                    </div>
                    <div>
                        <div className="text-xs text-brand-ink3">{t('invite.paying')}</div>
                        <div className="font-serif text-xl text-brand-ink mt-0.5" data-testid="invite-paying-count">{paying}<span className="text-brand-ink3 text-sm">/{threshold}</span></div>
                    </div>
                    <div>
                        <div className="text-xs text-brand-ink3">{t('invite.earned')}</div>
                        <div className="font-serif text-xl text-brand-green mt-0.5" data-testid="invite-days-earned">{daysEarned} <span className="text-sm">{t('invite.days')}</span></div>
                    </div>
                </div>

                <div className="mt-3 h-1.5 bg-brand-bg2 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-green transition-all" style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-3 text-xs text-brand-ink3 text-center" data-testid="invite-next-reward">
                    {paying > 0 && towardNext === 0
                        ? t('invite.all_used')
                        : <>{t('invite.next_reward_pre')} <strong className="text-brand-green">{needForNext}</strong> {t('invite.next_reward_post')}</>}
                </div>
            </div>
        </section>
    );
}
